import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Activity,
  Database,
  Server,
  HardDrive,
  Network,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  healthApi,
  SystemHealth,
  DatabaseHealth,
  PerformanceMetrics,
} from "@/lib/healthApi";
import { useQuery } from "@tanstack/react-query";

const AdminSystemHealth: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Check if user is admin
  const isAdmin = isAuthenticated && user?.role === "ADMIN";

  // Fetch system health data
  const {
    data: systemHealth,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ["system-health"],
    queryFn: healthApi.getSystemHealth,
    enabled: isAdmin,
    refetchInterval: autoRefresh ? 30000 : false, // Refresh every 30 seconds if auto-refresh is enabled
  });

  // Fetch database health data
  const {
    data: databaseHealth,
    isLoading: dbLoading,
    error: dbError,
    refetch: refetchDatabase,
  } = useQuery({
    queryKey: ["database-health"],
    queryFn: healthApi.getDatabaseHealth,
    enabled: isAdmin,
    refetchInterval: autoRefresh ? 30000 : false,
  });

  // Fetch performance metrics
  const {
    data: performanceMetrics,
    isLoading: perfLoading,
    error: perfError,
    refetch: refetchPerformance,
  } = useQuery({
    queryKey: ["performance-metrics"],
    queryFn: healthApi.getPerformanceMetrics,
    enabled: isAdmin,
    refetchInterval: autoRefresh ? 30000 : false,
  });

  // Get status icon and color
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "degraded":
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "unhealthy":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "healthy":
        return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
      case "degraded":
        return (
          <Badge className="bg-yellow-100 text-yellow-800">Degraded</Badge>
        );
      case "unhealthy":
        return <Badge className="bg-red-100 text-red-800">Unhealthy</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Format uptime
  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                Please log in to access the system health dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                You need admin privileges to access the system health dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 sm:mb-8"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500">
                <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                  System Health
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Monitor system performance and health metrics
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="text-xs sm:text-sm"
              >
                <RefreshCw
                  className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${
                    autoRefresh ? "animate-spin" : ""
                  }`}
                />
                <span className="hidden sm:inline">Auto Refresh</span>
                <span className="sm:hidden">Auto</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  refetchHealth();
                  refetchDatabase();
                  refetchPerformance();
                }}
                className="text-xs sm:text-sm"
              >
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
                <span className="sm:hidden">Refresh</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                className="text-xs sm:text-sm"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to Admin</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </div>
          </motion.div>

          {/* Overall System Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-4 sm:mb-6"
          >
            <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  {systemHealth && getStatusIcon(systemHealth.status)}
                  System Status
                </CardTitle>
                <CardDescription className="text-sm">
                  Overall system health and performance overview
                </CardDescription>
              </CardHeader>
              <CardContent>
                {healthLoading ? (
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                    <span className="text-sm sm:text-base">
                      Loading system status...
                    </span>
                  </div>
                ) : healthError ? (
                  <div className="text-red-600 text-center py-6 sm:py-8 text-sm sm:text-base">
                    Error loading system status
                  </div>
                ) : systemHealth ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    <div className="text-center">
                      <div className="text-lg sm:text-2xl font-bold mb-2">
                        {getStatusBadge(systemHealth.status)}
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Overall Status
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg sm:text-2xl font-bold mb-2">
                        {formatUptime(systemHealth.uptime)}
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Uptime
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg sm:text-2xl font-bold mb-2">
                        {new Date(systemHealth.timestamp).toLocaleTimeString()}
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Last Updated
                      </p>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </motion.div>

          {/* Detailed Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6"
          >
            {/* Database Health */}
            <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Database className="w-4 h-4 sm:w-5 sm:h-5" />
                  Database Health
                </CardTitle>
                <CardDescription className="text-sm">
                  Database connection and performance metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dbLoading ? (
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                    <span className="text-sm sm:text-base">
                      Loading database metrics...
                    </span>
                  </div>
                ) : dbError ? (
                  <div className="text-red-600 text-center py-6 sm:py-8 text-sm sm:text-base">
                    Error loading database metrics
                  </div>
                ) : databaseHealth ? (
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Status
                      </span>
                      {getStatusBadge(databaseHealth.status)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Response Time
                      </span>
                      <span className="text-xs sm:text-sm">
                        {databaseHealth.responseTime}ms
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Active Connections
                      </span>
                      <span className="text-xs sm:text-sm">
                        {databaseHealth.activeConnections}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Total Queries
                      </span>
                      <span className="text-xs sm:text-sm">
                        {databaseHealth.totalQueries.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Server className="w-4 h-4 sm:w-5 sm:h-5" />
                  Performance Metrics
                </CardTitle>
                <CardDescription className="text-sm">
                  System resource usage and performance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {perfLoading ? (
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                    <span className="text-sm sm:text-base">
                      Loading performance metrics...
                    </span>
                  </div>
                ) : perfError ? (
                  <div className="text-red-600 text-center py-6 sm:py-8 text-sm sm:text-base">
                    Error loading performance metrics
                  </div>
                ) : performanceMetrics ? (
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-medium">
                          Memory Usage
                        </span>
                        <span className="text-xs sm:text-sm">
                          {performanceMetrics.memory.heapUsed}MB /{" "}
                          {performanceMetrics.memory.heapTotal}MB
                        </span>
                      </div>
                      <Progress
                        value={
                          (performanceMetrics.memory.heapUsed /
                            performanceMetrics.memory.heapTotal) *
                          100
                        }
                        className="h-2"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Uptime
                      </span>
                      <span className="text-xs sm:text-sm">
                        {formatUptime(performanceMetrics.uptime)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Node Version
                      </span>
                      <span className="text-xs sm:text-sm">
                        {performanceMetrics.nodeVersion}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-medium">
                        Platform
                      </span>
                      <span className="text-xs sm:text-sm">
                        {performanceMetrics.platform}
                      </span>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </motion.div>

          {/* Service Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Network className="w-4 h-4 sm:w-5 sm:h-5" />
                  Service Status
                </CardTitle>
                <CardDescription className="text-sm">
                  Status of all system services and components
                </CardDescription>
              </CardHeader>
              <CardContent>
                {systemHealth ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg border">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Database className="w-4 h-4 sm:w-5 sm:h-5" />
                        <div>
                          <div className="font-medium text-sm sm:text-base">
                            Database
                          </div>
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            PostgreSQL
                          </div>
                        </div>
                      </div>
                      {systemHealth.services.database ? (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg border">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <HardDrive className="w-4 h-4 sm:w-5 sm:h-5" />
                        <div>
                          <div className="font-medium text-sm sm:text-base">
                            Storage
                          </div>
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            File Storage
                          </div>
                        </div>
                      </div>
                      {systemHealth.services.storage ? (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg border sm:col-span-2 lg:col-span-1">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
                        <div>
                          <div className="font-medium text-sm sm:text-base">
                            Email
                          </div>
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            Email Service
                          </div>
                        </div>
                      </div>
                      {systemHealth.services.email ? (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm sm:text-base">
                    No service status data available
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemHealth;
